import { Component, inject, signal } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { Button } from 'primeng/button';
import { ApiResponse } from '@core/api/apiresponse.model';
import { appConfig } from '@core/app-config';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { validateAllFormControls } from '@core/form-utils';
import { LetInputCprComponent } from '@shared/let-input-cpr/let-input-cpr.component';

@Component({
  selector: 'app-slet-revision',
  imports: [FormsModule, InputText, Message, ReactiveFormsModule, Button, LetInputCprComponent],
  templateUrl: './slet-revision.component.html',
  styleUrl: './slet-revision.component.css',
})
export class SletRevisionComponent {
  private httpClient = inject(HttpClient);

  loading = signal(false);
  sletningTilladt = signal(false);
  data = signal<Data | undefined>(undefined);
  validationMessage = signal<string | undefined>(undefined);
  besked = signal('');
  fejlbesked = signal('');

  form = new FormGroup({
    cpr: new FormControl('', {
      validators: [Validators.required],
    }),
    letsikringNr: new FormControl('', {
      validators: [Validators.required],
    }),
    revisionNr: new FormControl('', {
      validators: [Validators.required],
    }),
  });

  get cprIsInvalid() {
    return (
      this.form.controls.cpr.touched &&
      this.form.controls.cpr.dirty &&
      this.form.controls.cpr.invalid
    );
  }

  get letsikringNrIsInvalid() {
    return (
      this.form.controls.letsikringNr.touched &&
      this.form.controls.letsikringNr.dirty &&
      this.form.controls.letsikringNr.invalid
    );
  }

  get revisionNrIsInvalid() {
    return (
      this.form.controls.revisionNr.touched &&
      this.form.controls.revisionNr.dirty &&
      this.form.controls.revisionNr.invalid
    );
  }

  async sletRevision(udfoerSletning = 0) {
    this.validationMessage.set(undefined);
    validateAllFormControls(this.form);
    if (this.form.invalid) {
      return;
    }

    const submitData = {
      ...(this.form.value as Data),
      udfoerSletning: udfoerSletning,
      cpr: this.form.value.cpr!.replaceAll('-', ''),
    };

    this.besked.set('');
    this.loading.set(true);
    try {
      const source = this.httpClient.get<
        ApiResponse<{ success: boolean; message: string }>
      >(`${appConfig.apiRoot}/api/support/sletrevision`, {
        params: submitData,
      });
      const response = await firstValueFrom(source);

      if (!response.success) {
        this.sletningTilladt.set(false);
        this.validationMessage.set(response.message);
      } else {
        if (udfoerSletning === 1) {
          this.sletningTilladt.set(false);
        } else {
          this.sletningTilladt.set(true);
        }
        if (response.message) {
          this.besked.set(response.message);
        }
      }
    } catch (e) {
      console.error(e);
      if (e instanceof Error) {
        this.validationMessage.set(e.message);
      }
    } finally {
      this.loading.set(false);
    }
  }

  fortryd() {
    this.form.reset();
  }
}

interface Data {
  udfoerSletning: number;
  cpr: string;
  letsikringNr: string;
  revisionNr: string;
}
